<template>
  <PageHeader
    :title="
      hasMultipleDashboards(userStore.roles) ? $t('ui.entities.user.role.salesExecutive') : $t('ui.entities.dashboard')
    "
  >
    <template #right>
      <span class="text-secondary">Last 30 Days</span>
    </template>
  </PageHeader>
  <div class="grid sm:grid-cols-1 md:grid-cols-3 md:gap-8">
    <Card :is-loading="fetchDataRequest.isLoading.value">
      <CardBody>
        <CardTitle title="Today's Appointments" />
        <ListGroup v-if="dashboardData.todaysAppointments.length > 0">
          <ListGroupItem
            v-for="app in dashboardData.todaysAppointments"
            :key="app.id"
            has-chevron
            hover-effect
            :route="'/agreement/' + app.agreementId + '/appointments/' + app.id"
          >
            <div>
              <Avatar
                size="md"
                :icon="getDictionaryItem('appointmentMethod', app.method).icon"
                rounding="rounded"
                color="secondary"
                no-border
              />
            </div>
            <div class="w-full ml-4">
              <div class="text-md font-medium w-full">
                {{ (app.forename + ' ' + app.surname + ' ' + app.company).trim() }}
              </div>
              <div class="text-sm text-secondary">{{ dayjs(app.time).format('h:ma') }}</div>
            </div>
          </ListGroupItem>
        </ListGroup>
        <div
          v-else
          class="text-secondary"
        >
          No appointments today
        </div>
      </CardBody>
    </Card>

    <div class="col-span-2">
      <Card :is-loading="fetchDataRequest.isLoading.value">
        <CardBody class="grid grid-cols-3">
          <div class="flex items-center">
            <Avatar
              size="lg"
              icon="calendar"
              color="brand"
              no-border
            />
            <div class="flex flex-col ml-4">
              <span class="text-xl font-medium leading-5">{{ dashboardData.appointmentsOccurred }}</span>
              <span class="text-sm text-secondary">Appointments</span>
            </div>
          </div>
          <div class="flex items-center">
            <Avatar
              size="lg"
              icon="check"
              color="success"
              no-border
            />
            <div class="flex flex-col ml-4">
              <span class="text-xl font-medium leading-5">{{ dashboardData.appointmentsRenewed }}</span>
              <span class="text-sm text-secondary">Renewed</span>
            </div>
          </div>
          <div class="flex items-center">
            <Avatar
              size="lg"
              icon="car"
              color="warning"
              no-border
            />
            <div class="flex flex-col ml-4">
              <span class="text-xl font-medium leading-5">
                {{ numberFormatter(dashboardData.valueRenewed, 'number', 0) }}
              </span>
              <span class="text-sm text-secondary">Value</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
import { useUserStore } from '@/stores/UserStore.ts'
import { getDictionaryItem } from '@/dictionary'
import { hasMultipleDashboards } from '@/utilities/dashboards'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'

import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Avatar from '@/components/avatar/Avatar.vue'

const userStore = useUserStore()
const userSelected = ref(userStore.details.id)

const defaultDashboardData = {
  todaysAppointments: [],
  appointmentsOccurred: 0,
  appointmentsRenewed: 0,
  valueRenewed: 0,
  currentConversionRate: 0
}

const dashboardData = ref(defaultDashboardData)

const fetchDataRequest = useApiRequest()

function getDashboardData() {
  // Reset the dashboard data
  dashboardData.value = defaultDashboardData

  fetchDataRequest
    .send({
      endpoint: '/v1/dashboards/sales-executive',
      method: 'get',
      params: {
        userId: userSelected.value,
        start: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dashboardData.value = response.data
    })
}
getDashboardData()

// fetch the data when the selected dealership changes
watch(
  userSelected,
  () => {
    getDashboardData()
  },
  { deep: true }
)
</script>

<style></style>
